/**
 * UrlImportModal — multi-step flow for importing clothing from a webpage URL.
 *
 * States:
 *   1. URL_INPUT      — Paste URL field, scan button
 *   2. EXTRACTING     — Loading spinner ("Fetching page...")
 *   3. AI_CLASSIFYING — AI classifies which images are clothing
 *   4. RESULTS        — Grid of ExtractedItemCards with select/deselect
 *   5. IMPORTING      — Progress bar importing selected items
 *
 * The backend extracts images from the page, then the webBrain service
 * classifies which are clothing items using AI.
 */

import { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Dimmer, Loader, Icon, Button } from 'semantic-ui-react';
import {
  useCreateClothingFromUrlMutation,
  useExtractClothingFromUrlMutation
} from '../../redux/apis/endpoints/closetApi';
import ExtractedItemCard from './ExtractedItemCard';
import './UrlImportModal.css';

const MODAL_STATE = {
  URL_INPUT: 'url_input',
  EXTRACTING: 'extracting',
  RESULTS: 'results',
  IMPORTING: 'importing'
};

const UrlImportModal = ({ open, onClose, onImportSuccess, initialUrl = '' }) => {
  const [state, setState] = useState(MODAL_STATE.URL_INPUT);
  const [url, setUrl] = useState('');
  const [error, setError] = useState(null);
  const [extractedImages, setExtractedImages] = useState([]);
  const [classifiedItems, setClassifiedItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [itemNames, setItemNames] = useState({});
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [scanMessage, setScanMessage] = useState('Fetching page...');

  const [createClothingFromUrl] = useCreateClothingFromUrlMutation();
  const [extractClothingFromUrl] = useExtractClothingFromUrlMutation();

  // Reset state when modal opens
  const handleOpen = useCallback(() => {
    setState(MODAL_STATE.URL_INPUT);
    setUrl(initialUrl || '');
    setError(null);
    setExtractedImages([]);
    setClassifiedItems([]);
    setSelectedItems(new Set());
    setItemNames({});
    setImportProgress({ done: 0, total: 0 });
    setScanMessage('Fetching page...');
  }, [initialUrl]);

  useEffect(() => {
    if (open) {
      handleOpen();
      // If opened with an initial URL from share intent, automatically start scanning
      if (initialUrl) {
        // Use a small timeout to let the state settle
        setTimeout(() => {
          handleScan(initialUrl);
        }, 100);
      }
    }
  }, [open, handleOpen, initialUrl]);

  // Validate URL
  const isValidUrl = (str) => {
    try {
      const u = new URL(str);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Step 1 → 2: Extract images from URL
  const handleScan = async (urlToScan = url) => {
    if (!urlToScan.trim()) {
      setError('Please enter a URL');
      return;
    }

    let scanUrl = urlToScan.trim();
    // Auto-prepend https:// if missing protocol
    if (!scanUrl.startsWith('http://') && !scanUrl.startsWith('https://')) {
      scanUrl = 'https://' + scanUrl;
      if (urlToScan === url) {
        setUrl(scanUrl);
      }
    }

    if (!isValidUrl(scanUrl)) {
      setError('Please enter a valid URL');
      return;
    }

    try {
      setError(null);
      setState(MODAL_STATE.EXTRACTING);
      setScanMessage('Fetching page...');

      const data = await extractClothingFromUrl(scanUrl).unwrap();

      const images = data.images || [];
      const pageTitle = data.pageTitle || '';

      if (images.length === 0) {
        setError('No images found on this page.');
        setExtractedImages([]);
        setClassifiedItems([]);
        setState(MODAL_STATE.RESULTS);
        return;
      }

      setExtractedImages(images);

      // Skip AI classification and just show all extracted images
      const allItems = images.map((img, i) => ({
        image: img,
        name: img.alt || `Extracted Image ${i + 1}`,
        confidence: 'unknown',
        originalIndex: i
      }));

      setClassifiedItems(allItems);
      
      // Select all by default
      const defaultSelected = new Set(allItems.map((_, i) => i));
      setSelectedItems(defaultSelected);
      setState(MODAL_STATE.RESULTS);
    } catch (err) {
      setError(err.message || 'Failed to extract images from URL');
      setState(MODAL_STATE.URL_INPUT);
    }
  };

  // Toggle item selection
  const handleToggleItem = (index) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Select/deselect all
  const handleSelectAll = () => {
    if (selectedItems.size === classifiedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(classifiedItems.map((_, i) => i)));
    }
  };

  // Update item name
  const handleNameChange = (index, newName) => {
    setItemNames(prev => ({ ...prev, [index]: newName }));
  };

  // Import selected items
  const handleImport = async () => {
    const toImport = classifiedItems.filter((_, i) => selectedItems.has(i));
    if (toImport.length === 0) return;

    setState(MODAL_STATE.IMPORTING);
    setImportProgress({ done: 0, total: toImport.length });
    let successCount = 0;

    for (let i = 0; i < toImport.length; i++) {
      const item = toImport[i];
      const itemName = itemNames[classifiedItems.indexOf(item)] || item.name;
      try {
        await createClothingFromUrl({
          imageUrl: item.image.url,
          clothesName: itemName
        }).unwrap();
        successCount++;
      } catch (err) {
        console.warn(`Failed to import item: ${itemName}`, err);
      }
      setImportProgress({ done: i + 1, total: toImport.length });
    }

    if (successCount > 0 && onImportSuccess) {
      onImportSuccess(successCount);
    }
    setState(MODAL_STATE.URL_INPUT);
    if (onClose) onClose();
  };

  // Handle Enter key in URL input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  const isBlocking = state === MODAL_STATE.EXTRACTING || state === MODAL_STATE.IMPORTING;

  if (!open) return null;

  const modalContent = (
    <div 
      className="url-import-modal-overlay" 
      onClick={isBlocking ? undefined : onClose}
    >
      <div 
        className="url-import-modal-container" 
        onClick={e => e.stopPropagation()}
      >
        {!isBlocking && onClose && (
          <div className="url-import-close-icon" onClick={onClose}>
            <Icon name="close" size="large" />
          </div>
        )}
        
        {/* ── URL INPUT STATE ── */}
        {state === MODAL_STATE.URL_INPUT && (
          <div className="url-import-content">
            <div className="url-import-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#00b5ad" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            </div>

            <h3 className="url-import-title">Import from URL</h3>
            <p className="url-import-description">
              Paste a product page or fashion website URL and we'll extract clothing images using AI.
            </p>

            {error && (
              <div className="url-import-error">
                <Icon name="warning circle" />
                {error}
              </div>
            )}

            <input
              className="url-input-field"
              type="url"
              placeholder="https://example.com/product/dress"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />

            <button
              className="url-scan-btn"
              onClick={handleScan}
              disabled={!url.trim()}
            >
              <Icon name="search" />
              Scan Page
            </button>
          </div>
        )}

        {/* ── EXTRACTING STATE ── */}
        {state === MODAL_STATE.EXTRACTING && (
          <div className="url-import-content url-import-scanning">
            <div className="url-scanning-spinner">
              <Dimmer active inverted>
                <Loader size="medium">{scanMessage}</Loader>
              </Dimmer>
            </div>

            <div className="url-scanning-phases">
              <div className="url-scanning-phase active">
                <Icon name="globe" />
                <span>Fetching page content...</span>
              </div>
              <div className="url-scanning-phase">
                <Icon name="images" />
                <span>Extracting images</span>
              </div>
            </div>
          </div>
        )}

        {/* ── RESULTS STATE ── */}
        {state === MODAL_STATE.RESULTS && (
          <div className="url-import-content url-import-results">
            <div className="url-results-header">
              <h3 className="url-import-title">
                {classifiedItems.length > 0
                  ? `Found ${classifiedItems.length} item${classifiedItems.length !== 1 ? 's' : ''}`
                  : 'No items found'
                }
              </h3>
              {classifiedItems.length > 0 && (
                <button className="url-select-all-link" onClick={handleSelectAll}>
                  {selectedItems.size === classifiedItems.length ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>

            {classifiedItems.length > 0 ? (
              <>
                <div className="extracted-items-grid">
                  {classifiedItems.map((item, i) => (
                    <ExtractedItemCard
                      key={`${item.image.url}-${i}`}
                      image={item.image}
                      selected={selectedItems.has(i)}
                      suggestedName={item.name}
                      onToggle={() => handleToggleItem(i)}
                      onNameChange={(name) => handleNameChange(i, name)}
                    />
                  ))}
                </div>

                <div className="url-results-actions">
                  <Button
                    color="teal"
                    fluid
                    onClick={handleImport}
                    disabled={selectedItems.size === 0}
                  >
                    <Icon name="plus" />
                    Import {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} to Closet
                  </Button>
                  <button className="url-back-link" onClick={() => setState(MODAL_STATE.URL_INPUT)}>
                    ← Try another URL
                  </button>
                </div>
              </>
            ) : (
              <div className="url-results-empty">
                {error ? (
                  <div style={{ padding: '20px', background: '#f5f5f5', borderRadius: '8px', marginBottom: '16px', color: '#444' }}>
                    <Icon name="info circle" size="large" style={{ marginBottom: '12px', color: '#999' }} />
                    <p style={{ margin: 0, fontSize: '15px' }}>{error}</p>
                  </div>
                ) : (
                  <p>No clothing items could be identified on this page.</p>
                )}
                <button className="url-back-link" onClick={() => setState(MODAL_STATE.URL_INPUT)}>
                  ← Try another URL
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── IMPORTING STATE ── */}
        {state === MODAL_STATE.IMPORTING && (
          <div className="url-import-content url-import-importing">
            <Dimmer active inverted>
              <Loader size="medium">
                Adding to closet ({importProgress.done}/{importProgress.total})...
              </Loader>
            </Dimmer>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default UrlImportModal;
